import type {
  Discipline,
  IssueStatus,
  PMIssue,
  PMIssueFilters,
  UserProfile,
} from "@/domains/pm/types";
import type { StagedAttachment } from "@/domains/pm/components/issues/IssueAttachmentsStager";
import { toISODate } from "@/domains/pm/utils/localDate";

/**
 * An issue being written that does not exist yet — the New issue dialog's
 * fields, and the /issues page's draft panel. Everything the create sequence
 * (`createIssueWithAssociations`) needs, and nothing that needs an issue id.
 *
 * Owned by the caller: the dialog keeps it in component state, the /issues
 * page in `IssuesStore.drafts`. `IssueDraftContent` only renders it.
 */
export interface IssueDraftValues {
  /** The issue's title — labelled "Description" in the UI, which is what people call it. */
  title: string;
  status: IssueStatus;
  /**
   * The project the issue is filed in — the entry point's, never chosen in the
   * form. Null only when the entry point had none; the save then refuses.
   */
  projectId: number | null;
  assigneeIds: number[];
  /** ISO date; "" = unset (the field grid's convention). */
  startDate: string;
  dueDate: string;
  disciplineIds: number[];
  parentIssueId: number | null;
  /** null = no checklist; [] = "Add checklist" chosen, nothing staged yet. */
  checklistItems: string[] | null;
  /** Documents linked once the issue exists. Uploads already exist as documents. */
  stagedAttachments: StagedAttachment[];
  /** Save the 3D view with the issue — only meaningful with a live section cut. */
  saveViewpoint: boolean;
}

/** What a draft opens with. Anything left out takes the New issue form's default. */
export interface IssueDraftDefaults {
  title?: string;
  status?: IssueStatus;
  projectId?: number | null;
  assigneeIds?: number[];
  disciplineIds?: number[];
  parentIssueId?: number | null;
  stagedAttachments?: StagedAttachment[];
  /** Defaults to today: an undated issue lands in the Gantt with no place on the axis. */
  startDate?: string;
}

/**
 * How an edit reached the draft. A `correction` is not the person's input: the
 * form fixing up a default it cannot keep (an assignee who is not a member of
 * the project). The yardstick moves with it, so an untouched draft stays
 * untouched — see `correctDraftDefaults`.
 */
export interface IssueDraftChangeMeta {
  correction?: boolean;
}

/** The status a new issue starts on unless the entry point says otherwise. */
export const DEFAULT_DRAFT_STATUS: IssueStatus = "in_progress";

export function initialDraftValues(
  defaults: IssueDraftDefaults = {},
  today: Date = new Date()
): IssueDraftValues {
  return {
    title: defaults.title ?? "",
    status: defaults.status ?? DEFAULT_DRAFT_STATUS,
    projectId: defaults.projectId ?? null,
    assigneeIds: [...(defaults.assigneeIds ?? [])],
    startDate: defaults.startDate ?? toISODate(today),
    dueDate: "",
    disciplineIds: [...(defaults.disciplineIds ?? [])],
    parentIssueId: defaults.parentIssueId ?? null,
    checklistItems: null,
    // Copied: the stager appends to this list, and the caller's array is often
    // a memoised prop shared with the next time the form opens.
    stagedAttachments: (defaults.stagedAttachments ?? []).map((a) => ({ ...a })),
    saveViewpoint: true,
  };
}

/** A title of only whitespace is no title — `pm_issues.title` would be blank. */
export function draftTitleIsEmpty(values: Pick<IssueDraftValues, "title">): boolean {
  return values.title.trim() === "";
}

function sameIds(a: readonly number[], b: readonly number[]): boolean {
  if (a.length !== b.length) return false;
  const set = new Set(a);
  return b.every((id) => set.has(id));
}

/**
 * Did the person enter or change anything BESIDES the title, compared with what
 * the draft opened with? The defaults an entry point fills in (a kanban
 * column's status, a Gantt band's assignee, today's start date) are not input:
 * a draft holding only those is still empty.
 *
 * Deliberately ignored: the title (asked separately, see `draftLeaveAction`),
 * and `saveViewpoint`, which modifies how a save happens rather than what the
 * issue says. An opened checklist with nothing staged in it is no input either.
 */
export function draftHasUserInput(values: IssueDraftValues, initial: IssueDraftValues): boolean {
  const items = values.checklistItems ?? [];
  const initialItems = initial.checklistItems ?? [];
  return (
    values.status !== initial.status ||
    values.projectId !== initial.projectId ||
    !sameIds(values.assigneeIds, initial.assigneeIds) ||
    values.startDate !== initial.startDate ||
    values.dueDate !== initial.dueDate ||
    !sameIds(values.disciplineIds, initial.disciplineIds) ||
    values.parentIssueId !== initial.parentIssueId ||
    items.length !== initialItems.length ||
    items.some((text, i) => text !== initialItems[i]) ||
    !sameIds(
      values.stagedAttachments.map((a) => a.id),
      initial.stagedAttachments.map((a) => a.id)
    )
  );
}

/**
 * A correction (see `IssueDraftChangeMeta`) applied to what the draft opened
 * with, so it is not mistaken for input. Only removals are corrections today —
 * the assignees the form drops for want of project access — so the same ids
 * leave the defaults; anything the person added stays input.
 */
export function correctDraftDefaults(
  initial: IssueDraftValues,
  before: IssueDraftValues,
  patch: Partial<IssueDraftValues>
): IssueDraftValues {
  if (!patch.assigneeIds) return initial;
  const kept = new Set(patch.assigneeIds);
  const dropped = new Set(before.assigneeIds.filter((id) => !kept.has(id)));
  if (dropped.size === 0) return initial;
  return { ...initial, assigneeIds: initial.assigneeIds.filter((id) => !dropped.has(id)) };
}

/**
 * Whether leaving the draft would lose anything worth asking about — the tab
 * closing, for one, where an asynchronous save cannot be relied on to finish.
 */
export function draftHasContent(values: IssueDraftValues, initial: IssueDraftValues): boolean {
  return !draftTitleIsEmpty(values) || draftHasUserInput(values, initial);
}

/**
 * What leaving a draft does — closing its panel, opening another issue,
 * starting another draft, changing project scope, navigating away:
 * - `commit`: it has a title, so it is saved;
 * - `discard`: nothing was entered, so it goes without a word;
 * - `confirm`: something was entered but there is no title to save it under,
 *   so the person is asked to add one or discard the issue.
 */
export type DraftLeaveAction = "commit" | "discard" | "confirm";

export function draftLeaveAction(
  values: IssueDraftValues,
  initial: IssueDraftValues
): DraftLeaveAction {
  if (!draftTitleIsEmpty(values)) return "commit";
  return draftHasUserInput(values, initial) ? "confirm" : "discard";
}

/**
 * `draftLeaveAction` for a draft the store holds. A draft whose issue already
 * exists (a save that failed after the insert) is never dropped without a word:
 * there is a real issue behind it, and discarding it deletes that issue.
 */
export function storedDraftLeaveAction(
  draft: Pick<IssueDraft, "values" | "initial" | "createdIssueId">
): DraftLeaveAction {
  const action = draftLeaveAction(draft.values, draft.initial);
  return action === "discard" && draft.createdIssueId != null ? "confirm" : action;
}

/**
 * Would the server's list hold this newly created issue under `filters`? Used
 * to put a saved draft's issue straight into the loaded list; anything the row
 * alone cannot answer (a text search, linked documents) says no, and the reload
 * that follows decides. A new issue has no comments, so a comments or mentions
 * filter can be answered.
 */
export function newIssueMatchesFilters(issue: PMIssue, filters: PMIssueFilters): boolean {
  const anyOf = (wanted: readonly number[] | undefined, have: readonly number[]) =>
    !wanted?.length || have.some((id) => wanted.includes(id));
  if (filters.status?.length && !filters.status.includes(issue.status)) return false;
  if (filters.buildingProjectId != null && filters.buildingProjectId !== issue.building_project_id)
    return false;
  if (
    !anyOf(
      filters.assigneeUserIds,
      (issue.assignees ?? []).map((u) => u.id)
    )
  )
    return false;
  if (
    !anyOf(
      filters.disciplineIds,
      (issue.disciplines ?? []).map((d) => d.id)
    )
  )
    return false;
  if (!anyOf(filters.createdByUserIds, [issue.created_by])) return false;
  if (filters.dueBefore && !(issue.due_date && issue.due_date < filters.dueBefore)) return false;
  if (filters.mentionedUserId != null || filters.hasComments === true) return false;
  if (filters.search?.trim() || filters.hasLinkedDocuments != null) return false;
  return true;
}

/**
 * Draft ids are negative, so they can never collide with a real issue's and a
 * view can tell a draft row from an issue by id alone. Each draft takes the next
 * one down, so a draft still being saved and the one opened after it differ.
 */
export function isDraftIssueId(id: number): boolean {
  return id < 0;
}

export function isDraftIssue(issue: Pick<PMIssue, "id">): boolean {
  return isDraftIssueId(issue.id);
}

/**
 * Sorts first under the List's default (issue number, newest first). Views
 * still pin a draft rather than trusting this — any other sort would move it,
 * and it must never be rendered as a number.
 */
export const DRAFT_ISSUE_NUMBER = Number.MAX_SAFE_INTEGER;

/** What the projection needs to show chips: the people and tags behind the ids. */
export interface DraftIssueLookups {
  users: readonly UserProfile[];
  disciplines: readonly Discipline[];
}

export interface DraftIssueProjectionInput {
  id: number;
  buildingId: number;
  orgId: string;
  createdBy: number;
  /** When the draft was opened — where the List's creation-date grouping files it. */
  createdAt: string;
  values: IssueDraftValues;
}

/**
 * The draft as a `PMIssue`, for the List, Kanban and Gantt to render beside the
 * real issues. Never put this in `IssuesStore.issues`: every write path keys on
 * the id, and reloads replace that array wholesale.
 *
 * Ids without a match in `lookups` are left out rather than invented — the
 * chips fill in when the project's members or the disciplines arrive.
 */
export function projectDraftIssue(
  input: DraftIssueProjectionInput,
  lookups: DraftIssueLookups
): PMIssue {
  const { values } = input;
  const usersById = new Map(lookups.users.map((u) => [u.id, u]));
  const disciplinesById = new Map(lookups.disciplines.map((d) => [d.id, d]));
  const items = values.checklistItems ?? [];
  return {
    id: input.id,
    building_id: input.buildingId,
    org_id: input.orgId,
    issue_number: DRAFT_ISSUE_NUMBER,
    title: values.title,
    status: values.status,
    start_date: values.startDate || null,
    due_date: values.dueDate || null,
    // 0 = no project (an entry point with none); no real project has that id.
    building_project_id: values.projectId ?? 0,
    protocol_row_id: null,
    parent_issue_id: values.parentIssueId,
    created_at: input.createdAt,
    created_by: input.createdBy,
    modified_at: null,
    modified_by: null,
    deleted_at: null,
    assignees: values.assigneeIds.flatMap((id) => usersById.get(id) ?? []),
    disciplines: values.disciplineIds.flatMap((id) => disciplinesById.get(id) ?? []),
    comments_count: 0,
    checklist_total: items.length,
    checklist_done: 0,
    unread_mention_count: 0,
    linked_documents: [],
    linked_elements: [],
    viewpoint: null,
    schedule_pinned: false,
    document_root: null,
  };
}

/**
 * Where a draft held by `IssuesStore` is:
 * - `editing`: open in the panel (the active draft) or waiting to be reopened;
 * - `committing`: its create sequence is running — still drawn, no longer editable;
 * - `failed`: the last commit threw; kept, with its retry keys, until reopened.
 */
export type IssueDraftPhase = "editing" | "committing" | "failed";

/** One /issues draft as the store holds it. Plain data; replaced, never mutated. */
export interface IssueDraft {
  /** Negative — see `isDraftIssueId`. */
  id: number;
  buildingId: number;
  /** Null when the organisation had not loaded; the commit then refuses. */
  orgId: string | null;
  createdBy: number | null;
  createdAt: string;
  values: IssueDraftValues;
  /** What it opened with — the yardstick for `draftHasUserInput`. */
  initial: IssueDraftValues;
  phase: IssueDraftPhase;
  /** The last commit failure, raw. The page words it (billingGuardMessage, friendlyIssueError). */
  error: unknown;
  /** Retry keys, kept across a failed commit so a retry never files a second issue. */
  createdIssueId: number | null;
  createdRow: PMIssue | null;
  checklistSavedFor: number | null;
  /** Every document uploaded while drafting, still staged or not: a discard bins them. */
  uploadedDocumentIds: number[];
  /**
   * An upload started in the panel has not landed yet. Leaving waits for it: a
   * save would go without the file, and a discard could not bin it.
   */
  uploading: boolean;
}

/** `projectDraftIssue` for a draft the store holds. */
export function projectStoredDraft(draft: IssueDraft, lookups: DraftIssueLookups): PMIssue {
  return projectDraftIssue(
    {
      id: draft.id,
      buildingId: draft.buildingId,
      orgId: draft.orgId ?? "",
      createdBy: draft.createdBy ?? 0,
      createdAt: draft.createdAt,
      values: draft.values,
    },
    lookups
  );
}
